/**
 * Pure helpers for the OAuth login bridge page (/oauth/connect).
 * See docs/roadmap/add-mcp/SPEC.Auth.md section 2.3-2.4 and packages/mm-mcp-server/README.md (INV-16..INV-18).
 */

/** sessionStorage key holding the AS's single-use `external_auth_id` while the user signs in. */
export const OAUTH_EXTERNAL_AUTH_ID_KEY = 'mm.oauth.externalAuthId';

/** Matches the server's accepted shape (opaque, printable ASCII, no whitespace, <= 512). */
const EXTERNAL_AUTH_ID = /^[\x21-\x7e]{1,512}$/;

/** The server rejects an `auth_time` older than 15 minutes; re-prompt a little earlier. */
export const MAX_CLIENT_AUTH_AGE_MS = 14 * 60 * 1000;

export function isValidExternalAuthId(v: string | null | undefined): v is string {
  return typeof v === 'string' && EXTERNAL_AUTH_ID.test(v);
}

/** The AS expires the id after 10 minutes; a stored id older than this is stale and ignored. */
export const EXTERNAL_AUTH_ID_TTL_MS = 10 * 60 * 1000;

export function readStoredExternalAuthId(now: number = Date.now()): string | null {
  try {
    const raw = window.sessionStorage.getItem(OAUTH_EXTERNAL_AUTH_ID_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: unknown; at?: unknown };
    if (typeof parsed.at !== 'number' || now - parsed.at > EXTERNAL_AUTH_ID_TTL_MS || parsed.at > now + 60_000) return null;
    return isValidExternalAuthId(parsed.id as string) ? (parsed.id as string) : null;
  } catch {
    return null;
  }
}

export function storeExternalAuthId(v: string, now: number = Date.now()): void {
  try {
    window.sessionStorage.setItem(OAUTH_EXTERNAL_AUTH_ID_KEY, JSON.stringify({ id: v, at: now }));
  } catch {
    /* sessionStorage unavailable: the page will report the missing id */
  }
}

export function clearExternalAuthId(): void {
  try {
    window.sessionStorage.removeItem(OAUTH_EXTERNAL_AUTH_ID_KEY);
  } catch {
    /* ignore */
  }
}

export type BridgeConfig = { completeUrl: string; issuerOrigin: string };

/**
 * Bridge endpoint and the only origin a returned redirect may point at, from
 * REACT_APP_MCP_URL (canonical MCP URL, e.g. https://mcp.stage.milestonemap.org/mcp) and
 * REACT_APP_MCP_OAUTH_ISSUER. Null when either is unset or invalid.
 */
export function getBridgeConfig(env: Record<string, string | undefined> = process.env): BridgeConfig | null {
  try {
    const mcp = new URL((env.REACT_APP_MCP_URL || '').trim());
    const issuer = new URL((env.REACT_APP_MCP_OAUTH_ISSUER || '').trim());
    return { completeUrl: new URL('/oauth/bridge/complete', mcp.origin).href, issuerOrigin: issuer.origin };
  } catch {
    return null;
  }
}

/** Defence in depth (the server already enforces this, INV-18): follow only https redirects on the issuer origin. */
export function isSafeRedirect(redirect: unknown, issuerOrigin: string): redirect is string {
  if (typeof redirect !== 'string') return false;
  try {
    const u = new URL(redirect);
    if (u.username || u.password) return false;
    const issuer = new URL(issuerOrigin);
    if (u.protocol !== 'https:' && !(u.protocol === 'http:' && issuer.protocol === 'http:')) return false;
    return u.origin === issuer.origin;
  } catch {
    return false;
  }
}
